import { supabase } from "@/integrations/supabase/client";

type LogArgs = {
  route?: string;
  reason?: string;
  resource?: string;
  errorCode?: string;
  metadata?: Record<string, unknown>;
};

/** Window during which an identical denial is considered a duplicate. */
const DEDUPE_WINDOW_MS = 5000;
const recent = new Map<string, number>();

function shouldSkip(key: string): boolean {
  const now = Date.now();
  // Opportunistic cleanup
  if (recent.size > 200) {
    for (const [k, t] of recent) {
      if (now - t > DEDUPE_WINDOW_MS) recent.delete(k);
    }
  }
  const last = recent.get(key);
  if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return true;
  recent.set(key, now);
  return false;
}

/**
 * Logs a denied access attempt (403 / RLS rejection / missing role) into the
 * `access_denied_logs` audit table. Fire-and-forget — never throws.
 * Identical denials (same user + route + reason + resource + code) within
 * DEDUPE_WINDOW_MS are coalesced to avoid double-logging across middleware
 * (ProtectedRoute / RequireSuperAdmin) and downstream data calls.
 */
export async function logAccessDenied(args: LogArgs = {}) {
  try {
    const { data } = await supabase.auth.getUser();
    const route =
      args.route ??
      (typeof window !== "undefined"
        ? window.location.pathname + window.location.search
        : "unknown");

    const key = [
      data.user?.id ?? "anon",
      route,
      args.reason ?? "",
      args.resource ?? "",
      args.errorCode ?? "",
    ].join("|");
    if (shouldSkip(key)) return;

    await supabase.from("access_denied_logs").insert({
      user_id: data.user?.id ?? null,
      route,
      reason: args.reason ?? null,
      resource: args.resource ?? null,
      error_code: args.errorCode ?? null,
      user_agent:
        typeof navigator !== "undefined" ? navigator.userAgent : null,
      metadata: (args.metadata ?? null) as never,
    });
  } catch (e) {
    console.warn("Failed to log access denied:", e);
  }
}

/**
 * Returns true if the Supabase error looks like an RLS / permission rejection.
 * Postgres codes: 42501 (insufficient_privilege), PGRST301 (PostgREST RLS),
 * or generic "permission denied" / "row-level security" messages.
 */
export function isAccessDeniedError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as { code?: string; message?: string; status?: number };
  if (e.status === 401 || e.status === 403) return true;
  if (e.code === "42501" || e.code === "PGRST301" || e.code === "PGRST116")
    return false; // PGRST116 = no rows; not denial
  if (e.code === "42501") return true;
  const msg = (e.message ?? "").toLowerCase();
  return (
    msg.includes("permission denied") ||
    msg.includes("row-level security") ||
    msg.includes("violates row-level security")
  );
}
